import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Container, HtmlView, MTable, RepealableName } from '../../../../../../Components';
import { useIntl } from 'react-intl';
import { useAptorApi } from '../../../../../../Api';
import { useFilter } from '../../../../../../Components/ListFilter';
import { MTableToolbar, Query } from '@material-table/core';
import { Checkbox, Box, Typography } from '@material-ui/core';
import { ComplianceFilter } from "./ComplianceFilter";
import { useComplianceState } from "./ComplianceState";
import { IGridQueryRequest, OrderDirection } from "../../../../../../Api/AptorApi";
import { useStyles } from '../../../../LawPortal.styles';
import { ModuleContext } from '../../../../../../Context/ModuleContext/ModuleContext';
import { RequirementListFilter } from "../RequirementListFilter";
import { OwnNotes } from "../../../../../../Components/OwnNotes";
import moment from "moment";
import { Module, UserContext } from "../../../../../../Context/UserContext/UserContext";

type LawData = {
  id: number;
  number: string;
  name: string;
  changeEffectiveFrom?: Date;
  changeNumber?: string;
  effectiveTo?: Date;
  tags?: string;
};

interface IProps {
  selectedRows: any;
  setSelectedRows: any;
  type: any;
  companyUnit: any;
  showMarkAll?: boolean;
  selectedRowsData?: any[];
  setSelectedRowsData?: (data: any[]) => void;
}

export const ComplianceTable = ({ selectedRows, setSelectedRows, type, companyUnit, showMarkAll, selectedRowsData, setSelectedRowsData }: IProps) => {
  const { formatMessage } = useIntl();
  const { api } = useAptorApi();
  const [filters, setFilter] = useFilter();
  const tableRef = React.useRef<any>(null);
  const classes = useStyles();
  const {
    enableCompliance,
    isComplianceUserFor,
    isComplianceUserForreq
  } = useComplianceState();
  const { hasAccessToModule, applicationVersion, getProcessName, getAspectName, getTagName } = useContext(UserContext);
  const [visibleRows, setVisibleRows] = useState<LawData[]>([]);
  const isOldVersion = applicationVersion?.value === 1;

  useEffect(() => {
    tableRef.current && (tableRef.current as any).onQueryChange();
  }, [filters, tableRef]);

  const { setHeader } = useContext(ModuleContext);

  useEffect(() => {
    setHeader(
      formatMessage({
        id: isOldVersion ? 'law-portal.legal.compliance.header' : 'law-portal.legal.compliance.header',
        defaultMessage: isOldVersion ? 'Legal compliance control' : 'Evaluation control',
      }),
    );
  }, [setHeader, formatMessage, isOldVersion]);

  useEffect(() => {
    if (companyUnit !== null) {
      setFilter('companyUnit', [companyUnit.label]);
    }
  }, [companyUnit]);

  function handleRowSelect(row: any) {
    const id = row.id;
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId: any) => rowId !== id));
      if (setSelectedRowsData && selectedRowsData) {
        setSelectedRowsData(selectedRowsData.filter((r: any) => r.id !== id));
      }
    } else {
      setSelectedRows([...selectedRows, id]);
      if (setSelectedRowsData && selectedRowsData) {
        setSelectedRowsData([...selectedRowsData, row]);
      }
    }
  }

  const handleLawDataFetch = useCallback(
    async (query: Query<LawData>) => {
      const result = await api.searchLawPortalLaws<LawData>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'number',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
      if (showMarkAll && result?.data) setVisibleRows(result.data);
      // Auto-populate selectedRowsData for pre-selected rows (edit mode)
      if (setSelectedRowsData && selectedRowsData && result?.data) {
        const existingIds = new Set(selectedRowsData.map((r: any) => r.id));
        const newEntries = result.data.filter((row: any) => selectedRows.includes(row.id) && !existingIds.has(row.id));
        if (newEntries.length > 0) {
          setSelectedRowsData([...selectedRowsData, ...newEntries]);
        }
      }
      return result;
    },
    [api, filters, showMarkAll, selectedRows, selectedRowsData, setSelectedRowsData],
  );

  const handleRequirementDataFetch = useCallback(
    async (query: Query<LawData>) => {
      const result = await api.searchLawPortalRequirements<LawData>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'name',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
      if (showMarkAll && result?.data) setVisibleRows(result.data);
      // Auto-populate selectedRowsData for pre-selected rows (edit mode)
      if (setSelectedRowsData && selectedRowsData && result?.data) {
        const existingIds = new Set(selectedRowsData.map((r: any) => r.id));
        const newEntries = result.data.filter((row: any) => selectedRows.includes(row.id) && !existingIds.has(row.id));
        if (newEntries.length > 0) {
          setSelectedRowsData([...selectedRowsData, ...newEntries]);
        }
      }
      return result;
    },
    [api, filters, showMarkAll, selectedRows, selectedRowsData, setSelectedRowsData],
  );

  const renderDate = (date?: Date) => {
    return date ? moment(date).format('ll') : undefined;
  };
  // useEffect(() => {
  //   const updatedQueryParams = Object.keys(filters)
  //     .filter(key => filters[key]?.join() !== undefined && filters[key]?.join() !== "")
  //     .map(key => `${key}=${encodeURIComponent(filters[key]?.join())}`)
  //     .join('&');
  //   history.push({
  //     pathname: '',
  //     search: `?${updatedQueryParams}`,
  //   });
  // }, [filters, history]);
  
//  <ComplianceActions
//           complianceEnabled={complianceEnabled}
//           enableCompliance={enableCompliance}
//           disableCompliance={disableCompliance}
//           hasCompliance={hasCompliance}
//           startComplianceSession={startComplianceSession}
//         /> 

  const isRowCheckboxVisible = useCallback((row: any) => {
    if (hasAccessToModule(Module.Administration) || hasAccessToModule(Module.Organization)) return true;
    return type?.value === 0 || type?.id === 0 ? isComplianceUserFor(row.id) : isComplianceUserForreq(row.id);
  }, [hasAccessToModule, type, isComplianceUserFor, isComplianceUserForreq]);


    const ToolbarComponent = useCallback((props: any) => (
      <div className={classes.toolbar} style={{ display: 'flex', justifyContent: showMarkAll?'space-between':"end" }}>
              {showMarkAll && (
            <Box style={{ display: 'flex', alignItems: 'center', padding: '12px 24px', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
            <Checkbox
              checked={visibleRows.filter((r: any) => isRowCheckboxVisible(r)).length > 0 && visibleRows.filter((r: any) => isRowCheckboxVisible(r)).every(r => selectedRows.includes(r.id))}
              indeterminate={visibleRows.filter((r: any) => isRowCheckboxVisible(r)).length > 0 && visibleRows.filter((r: any) => isRowCheckboxVisible(r)).some(r => selectedRows.includes(r.id)) && !visibleRows.filter((r: any) => isRowCheckboxVisible(r)).every(r => selectedRows.includes(r.id))}
              onChange={() => {
                const selectableRows = visibleRows.filter((r: any) => isRowCheckboxVisible(r));
                const allSelected = selectableRows.length > 0 && selectableRows.every(r => selectedRows.includes(r.id));
                if (allSelected) {
                  const selectableIds = selectableRows.map(r => r.id);
                  setSelectedRows(selectedRows.filter((id: any) => !selectableIds.includes(id)));
                  if (setSelectedRowsData && selectedRowsData) {
                    setSelectedRowsData(selectedRowsData.filter((r: any) => !selectableIds.includes(r.id)));
                  }
                } else {
                  const newSelected = [...selectedRows];
                  const newSelectedData = selectedRowsData ? [...selectedRowsData] : [];
                  selectableRows.forEach(r => {
                    if (!newSelected.includes(r.id)) {
                      newSelected.push(r.id);
                      newSelectedData.push(r);
                    }
                  });
                  setSelectedRows(newSelected);
                  if (setSelectedRowsData) setSelectedRowsData(newSelectedData);
                }
              }}
              style={{ color: '#c4122f' }}
            />
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              {formatMessage({ id: 'law-portal.evalution.table.selectAll' })}
            </Typography>
          </Box>
        )}
        <MTableToolbar {...props} />
      </div>
    ), [showMarkAll, visibleRows, isRowCheckboxVisible, selectedRows, setSelectedRows, selectedRowsData, setSelectedRowsData, classes.toolbar, formatMessage]);
  return (
    <div className={classes.listWrapper}>
      <div>
        {type?.value === 0 || type?.id === 0 ?
          <ComplianceFilter setFilter={setFilter} filters={filters} /> :
          <RequirementListFilter setFilter={setFilter} filters={filters} />
        }
        {/* {showMarkAll && (
          <Box style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
            <Checkbox
              checked={visibleRows.filter((r: any) => isRowCheckboxVisible(r)).length > 0 && visibleRows.filter((r: any) => isRowCheckboxVisible(r)).every(r => selectedRows.includes(r.id))}
              indeterminate={visibleRows.filter((r: any) => isRowCheckboxVisible(r)).length > 0 && visibleRows.filter((r: any) => isRowCheckboxVisible(r)).some(r => selectedRows.includes(r.id)) && !visibleRows.filter((r: any) => isRowCheckboxVisible(r)).every(r => selectedRows.includes(r.id))}
              onChange={() => {
                const selectableRows = visibleRows.filter((r: any) => isRowCheckboxVisible(r));
                const allSelected = selectableRows.length > 0 && selectableRows.every(r => selectedRows.includes(r.id));
                if (allSelected) {
                  const selectableIds = selectableRows.map(r => r.id);
                  setSelectedRows(selectedRows.filter((id: any) => !selectableIds.includes(id)));
                  if (setSelectedRowsData && selectedRowsData) {
                    setSelectedRowsData(selectedRowsData.filter((r: any) => !selectableIds.includes(r.id)));
                  }
                } else {
                  const newSelected = [...selectedRows];
                  const newSelectedData = selectedRowsData ? [...selectedRowsData] : [];
                  selectableRows.forEach(r => {
                    if (!newSelected.includes(r.id)) {
                      newSelected.push(r.id);
                      newSelectedData.push(r);
                    }
                  });
                  setSelectedRows(newSelected);
                  if (setSelectedRowsData) setSelectedRowsData(newSelectedData);
                }
              }}
              style={{ color: '#c4122f' }}
            />
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              {formatMessage({ id: 'law-portal.evalution.table.selectAll' })}
            </Typography>
          </Box>
        )} */}
        <MTable
          tableRef={tableRef}
          columns={type?.value === 0 || type?.id === 0 ? [
            { title: formatMessage({ id: 'law-portal.our-laws.table.number' }), field: 'number' },
            {
              title: formatMessage({ id: 'law-portal.our-laws.table.law' }),
              field: 'name',
              render: (row) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
            },
            {
              title: formatMessage({ id: 'law-portal.our-laws.table.description' }),
              field: 'description',
              render: (row: any) => <HtmlView variant="body2" value={row?.description} />,
              sorting: false,
            },
            { title: formatMessage({ id: 'law-portal.our-laws.table.changeNumber' }), field: 'changeNumber' },
            {
              title: formatMessage({ id: 'law-portal.our-laws.table.changeEffectiveFrom' }),
              field: 'changeEffectiveFrom',
              render: (row) => renderDate(row.changeEffectiveFrom),
                hidden: true,
            },
            {
              title: formatMessage({ id: 'law-portal.our-laws.table.notes' }),
              field: 'notes',
              render: (row: any) => <OwnNotes {...row} />,
              sorting: false,
                hidden: true,
            },
            {
              title: formatMessage({ id: 'law-portal.compliance.report.table.col' }),
              field: 'responsible',
              render: (row: any) => {
                if (row.responsible) {
                  const responsiblearr = row?.responsible?.split(',');
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {responsiblearr?.map((val: any, index: number) => {
                        return <span key={index}>{val}</span>;
                      })}
                    </div>
                  );
                } else {
                  return "-";
                }
              },
              sorting: false,
            },
            {
              // title: formatMessage({ id: 'utils.filters.processes' }),
              title: getProcessName(formatMessage),
              field: 'processes',
              render: (row: any) => {
                if (row.processes) {
                  const processarr = row?.processes?.split(',');
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {processarr?.map((val: any, index: number) => {
                        return <span key={index}>{val}</span>;
                      })}
                    </div>
                  );
                } else {
                  return "-";
                }
              },
              sorting: false,
                hidden: true,
            },
            {
              // title: formatMessage({ id: 'utils.filters.aspects' }),
              title: getAspectName(formatMessage),
              field: 'aspects',
              render: (row: any) => {
                if (row.aspects) {
                  const aspectsarr = row?.aspects?.split(',');
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {aspectsarr?.map((val: any, index: number) => {
                        return <span key={index}>{val}</span>;
                      })}
                    </div>
                  );
                } else {
                  return "-";
                }
              },
              sorting: false,
                hidden: true,
            },
            {
              // title: formatMessage({ id: 'utils.filters.tags' }),
              title: getTagName(formatMessage),
              field: 'tags',
              render: (row: any) => {
                if (row.tags) {
                  const tagsarr = row?.tags?.split(',');
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {tagsarr?.map((val: any, index: number) => {
                        return <span key={index}>{val}</span>;
                      })}
                    </div>
                  );
                } else {
                  return "-";
                }
              },
              sorting: false,
                hidden: true,
            },
            { title: formatMessage({ id: 'law-portal.our-laws.table.area' }), field: 'area',  hidden: true, },
          ] : [
            {
              title: formatMessage({ id: 'law-portal.requirements.table.name' }),
              field: 'name',
              render: (row: any) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
            },
            {
              title: formatMessage({ id: 'law-portal.requirements.table.description' }),
              field: 'description',
              render: (row: any) => <HtmlView variant="body2" value={row.description} />,
              sorting: false,
            },
            { title: formatMessage({ id: 'law-portal.requirements.table.area' }), field: 'area',  hidden: true },
            {
              title: formatMessage({ id: 'law-portal.requirements.table.changedDate' }),
              field: 'changedDate',
              render: (row: any) => renderDate(row.changedDate),
              hidden: true,
            },
            {
              title: formatMessage({ id: 'law-portal.requirements.table.notes' }),
              field: 'notes',
              render: (row: any) => <OwnNotes {...row} />,
              sorting: false,
                hidden: true,
            },
            {
              title: formatMessage({ id: 'law-portal.compliance.report.table.col' }),
              field: 'responsible',
              render: (row: any) => {
                if (row.responsible) {
                  const responsiblearr = row?.responsible?.split(',');
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {responsiblearr?.map((val: any, index: number) => {
                        return <span key={index}>{val}</span>;
                      })}
                    </div>
                  );
                } else {
                  return "-";
                }
              },
              sorting: false,
            },
            {
              // title: formatMessage({ id: 'utils.filters.processes' }),
              title: getProcessName(formatMessage),
              field: 'processes',
              render: (row: any) => {
                if (row.processes) {
                  const processarr = row?.processes?.split(',');
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {processarr?.map((val: any, index: number) => {
                        return <span key={index}>{val}</span>;
                      })}
                    </div>
                  );
                } else {
                  return "-";
                }
              },
              sorting: false,
                hidden: true,
            },
            {
              // title: formatMessage({ id: 'utils.filters.aspects' }),
              title: getAspectName(formatMessage),
              field: 'aspects',
              render: (row: any) => {
                if (row.aspects) {
                  const aspectsarr = row?.aspects?.split(',');
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {aspectsarr?.map((val: any, index: number) => {
                        return <span key={index}>{val}</span>;
                      })}
                    </div>
                  );
                } else {
                  return "-";
                }
              },
              sorting: false,
                hidden: true,
            },
            {
              // title: formatMessage({ id: 'utils.filters.tags' }),
              title: getTagName(formatMessage),
              field: 'tags',
              render: (row: any) => {
                if (row.tags) {
                  const tagsarr = row?.tags?.split(',');
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {tagsarr?.map((val: any, index: number) => {
                        return <span key={index}>{val}</span>;
                      })}
                    </div>
                  );
                } else {
                  return "-";
                }
              },
              sorting: false,
                hidden: true,
            }
          ]}
          data={type?.value === 0 || type?.id === 0 ? handleLawDataFetch : handleRequirementDataFetch}
          // onRowClick={(_, row) => row && history.push(`/law-portal/our-laws/${row.id}`)}
          options={{
            actionsColumnIndex: 0
          }}
          actionLabel={enableCompliance ? formatMessage({ id: 'law-portal.our-laws.table.compliance' }) : undefined}
          actions={
            [
              (row: LawData) => ({
                icon: () => <Checkbox checked={selectedRows.includes(row.id)} />,
                onClick: () => handleRowSelect(row),
                hidden: (hasAccessToModule(Module.Administration) || hasAccessToModule(Module.Organization)) ? false : type?.value === 0 || type?.id === 0 ? !isComplianceUserFor(row.id) : !isComplianceUserForreq(row.id),
              }),
            ]
          }
          components={{
            Toolbar: ToolbarComponent,
          }}
        />
      </div>
    </div>
  );
};
